import {
  type MemberRow,
  buildAddMembersRequest,
  isRowComplete,
  isRowSendable,
  normalizeEmail,
} from '@/features/OrgMembers/lib/memberRows'
import type { CreateAccountRequest } from '@/generated/http-clients/v3ApiSchemas'

export type CreateAccountFormValues = {
  authEmail: string
  firstName: string
  lastName: string
  company: string
  jobTitle: string
  phone: string
}

type OwnerNames = {
  firstName: string
  lastName: string
}

export function canSubmitOwner(
  owner: MemberRow | undefined,
  ownerEmail: string,
  names: OwnerNames,
): boolean {
  if (!owner || normalizeEmail(ownerEmail) !== owner.key) {
    return false
  }
  switch (owner.status) {
    case 'new':
      return names.firstName.trim() !== '' && names.lastName.trim() !== ''
    case 'existing':
    case 'multiple':
      return isRowComplete(owner)
    default:
      return false
  }
}

export function canSubmitMembers(rows: readonly MemberRow[]): boolean {
  return (
    rows.every((row) => row.status !== 'pending') &&
    rows.filter(isRowSendable).every(isRowComplete)
  )
}

function ownerFields(
  values: CreateAccountFormValues,
  owner: MemberRow,
): Partial<CreateAccountRequest> {
  switch (owner.status) {
    case 'new':
      return {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
      }
    case 'existing':
      return { ownerUserId: owner.userId }
    case 'multiple':
      return { ownerUserId: owner.selectedUserId }
    default:
      return {}
  }
}

export function buildCreateAccountRequest({
  values,
  owner,
  memberRows,
}: {
  values: CreateAccountFormValues
  owner: MemberRow
  memberRows: readonly MemberRow[]
}): CreateAccountRequest {
  const body: CreateAccountRequest = {
    authEmail: values.authEmail.trim(),
    company: values.company.trim(),
    ...ownerFields(values, owner),
  }
  const jobTitle = values.jobTitle.trim()
  if (jobTitle) {
    body.jobTitle = jobTitle
  }
  const phone = values.phone.trim()
  if (phone) {
    body.phone = phone
  }
  const { members } = buildAddMembersRequest(memberRows)
  if (members.length > 0) {
    body.additionalMembers = members
  }
  return body
}
